import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import "./ClassDetails.css";
import { Layout, Row, Alert } from "antd";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";
import useInfiniteQuery from "hooks/useInfiniteQuery";
import useBannerBackground from "hooks/useBannerBackground";

// graphql
import GET_CLASS_DETAILS from "graphql/queries/GetClassDetails";
import GET_MORE_CLASSES from "graphql/queries/GetMoreClasses";

// custom components
import ClassList from "common/class_card/ClassList";

import ClassBanner from "common/class_banner";
import { CenteredLoading } from "common/loading";
import TopicDetails from "./TopicDetails";

import ClassDetailsContext from "./ClassDetailsContext";

const DEFAULT_PAGE_SIZE = 4;

const { Content } = Layout;

function ClassDetails() {
  const { t } = useTranslation("class_details");
  const params = useParams();
  const [showCourseWidth, setShowCourseWidth] = useState(0);

  const setWidthToClassCard = useCallback((width: number) => {
    setShowCourseWidth(width);
  }, []);

  const classDetailsCtx = useMemo(
    () => ({
      setWidthToClassCardDetails: (width: number) => setWidthToClassCard(width),
    }),
    [setWidthToClassCard]
  );

  /** DATA MANAGEMENT FOR CLASS DETAILS */
  const {
    loading,
    data,
    error,
    refetch: refetchClassDetails,
  } = useQuery(GET_CLASS_DETAILS, {
    fetchPolicy: "cache-and-network",
    variables: {
      id: params.classid,
    },
  });

  const { backgroundImage } = useBannerBackground();

  const {
    nodes: moreClasses,
    error: moreClassesError,
    pageInfo,
    loadMore,
  } = useInfiniteQuery("moreClasses", GET_MORE_CLASSES, {
    variables: {
      lessonId: params.classid,
      first: DEFAULT_PAGE_SIZE,
    },
  });

  // TODO:
  // Add error screen if there's an error
  if (error) {
    console.log(error);
    return <div style={{ marginTop: "64px" }}>{t("Error!")}</div>;
  }

  if (moreClassesError) {
    return (
      <Alert
        type="error"
        message="Error loading classes. Please check your internet or let us know if this keeps happening."
      />
    );
  }

  if (loading)
    return (
      <div style={{ marginTop: 64 }}>
        <Alert type="info" message={t("Loading...")} />
      </div>
    );

  const { currentClass, appointment, myTopic, myRating } =
    data?.classDetails || {};

  return (
    <ClassDetailsContext.Provider value={classDetailsCtx}>
      <Content className="content-container">
        <div className="class-details section" style={backgroundImage}>
          <div className="header">
            <div className="section-header">
              <span className="section-title white-text">
                {t("CLASS DETAILS")}
              </span>
            </div>
            {currentClass.course && (
              <div className="join-course" style={{ width: showCourseWidth }} />
            )}
          </div>
          <ClassBanner
            currentClass={currentClass}
            appointment={appointment}
            rating={myRating}
            setButtonRef={setShowCourseWidth}
          />
        </div>
        <TopicDetails
          topic={currentClass.topic}
          myTopic={myTopic}
          appointmentNote={appointment?.oooNotes}
          refresh={() => refetchClassDetails({ id: currentClass.id })}
        />

        <div className="past-classes section">
          <div className="section-header">
            <span className="section-title">{t("MORE CLASSES")}</span>
          </div>
          <InfiniteScroll
            pageStart={0}
            loadMore={loadMore}
            hasMore={pageInfo?.hasNextPage}
            loader={<CenteredLoading key={pageInfo?.startCursor} />}
          >
            <Row className="section-cards" gutter={24}>
              <ClassList lessons={moreClasses || []} allowsBooking />
            </Row>
          </InfiniteScroll>
        </div>
      </Content>
    </ClassDetailsContext.Provider>
  );
}

export default ClassDetails;
